/**
 * Turning a recurring campaign into today's blast — the generation half of
 * Scheduled Sends. See `claude/scheduled-sends-rebuild-scope.md` for the full
 * design; this file is the pure core of it, in the same shape as `plan()` in
 * `milestones.ts`: no I/O, no clock, every date and time handed in by the
 * caller as a plain string. That is what let the milestone engine be checked
 * against real data before it ever touched a database, and the same proof is
 * wanted here before this is trusted with a schedule.
 *
 * WHAT THIS DOES NOT DO
 *
 * It does not check whether today's blast has already been created. `id` is
 * built deterministically from the template and the date
 * (`manual-<date>-<slug>`), and `campaigns.id` is the primary key — so a
 * second attempt on the same day is a conflict the database refuses, not a
 * question this file needs to answer. Calling `shouldFireNow` and
 * `buildBlast` twice for the same template on the same day is meant to be
 * harmless; the caller (`functions/api/generate-scheduled.ts`) still checks
 * first, to skip the (comparatively expensive) audience resolution once
 * today's blast already exists, not because skipping it would be unsafe.
 *
 * WHY `is_enabled` AND `validateCadence` ARE CHECKED HERE TOO
 *
 * The create-flow UI (not yet built) will refuse to save a campaign whose
 * cadence does not validate, and will offer no way to save one disabled. Both
 * are re-checked here anyway, because "the UI would have stopped this" is not
 * the same claim as "this cannot happen" — a row can be edited directly, or
 * written by something else entirely, and the one thing worse than a
 * recurring campaign that fails to fire is one that fires on a rule nobody
 * validated.
 */

import type { Agent } from './types'
import type { FilterContext } from './filters'
import { type CadenceRule, firesOn, validateCadence } from './cadence'
import { type ScheduledAudienceRule, resolveScheduledAudienceIds } from './scheduled-audience'

export type RecurringCampaign = {
  /** The template's own id. Conventionally `sched-<slug>`, but nothing here
   *  requires that prefix — `slugFor` degrades gracefully if it's missing. */
  id: string
  name: string
  subject: string
  body: string
  cadence: CadenceRule
  audience_rule: ScheduledAudienceRule
  /**
   * `HH:MM`, 24-hour, America/New_York — reusing the existing `meeting_time`
   * column (see the migration note in `generate-scheduled.ts`) rather than
   * adding a new one. Not validated by `validateCadence`, which only knows
   * about the cadence union — checked here instead, with `TIME_RE`.
   */
  fire_time: string
  is_enabled: boolean
}

export type GeneratedBlast = {
  /** `manual-<date>-<slug>` — matches `MANUAL_ID` in `send-campaign.ts` by
   *  construction, so this needs no allowance added to that gate. */
  id: string
  name: string
  subject: string
  body: string
  /**
   * Exactly the shape `send-campaign.ts`'s `idsOf` already reads and
   * `campaigns.ts`'s `AudienceRule` already documents — **resolved once,
   * frozen from this instant on**, same guarantee as a one-off blast's
   * audience, just re-earned every time a new occurrence fires rather than
   * granted once at compose time. See `scheduled-sends-rebuild-scope.md` on
   * why that is the same rule and not a second one.
   */
  audience: { type: 'list'; agent_ids: string[]; from: string; resolved_at: string }
  /** The recurring template this came from — not read by the send path,
   *  kept for `campaigns.generated_from_id` so the two stay traceable. */
  source_campaign_id: string
  occurrence_date: string
}

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

/**
 * Is this campaign a recurring template at all — the same test used to
 * decide whether `generate-scheduled.ts` should look at it, and whether
 * `Scheduled.tsx` should list it. One answer, shared, rather than two copies
 * of "does this jsonb column hold anything" quietly drifting apart — `{}` is
 * the column default for an ordinary one-off blast, `cadence: {}` on purpose
 * on a GENERATED blast (see `buildBlast`), and anything else means a rule is
 * set.
 */
export function hasCadence(cadence: unknown): boolean {
  return !!cadence && typeof cadence === 'object' && Object.keys(cadence as object).length > 0
}

/**
 * `sched-team-call` -> `team-call`. Falls back to the whole id, lowercased
 * and stripped to what `MANUAL_ID` accepts, if it doesn't start with
 * `sched-` — so a template id typed or migrated without the prefix still
 * produces a usable child id rather than one silently rejected by the send
 * path's own gate.
 */
export function slugFor(templateId: string): string {
  const base = templateId.startsWith('sched-') ? templateId.slice('sched-'.length) : templateId
  return base.toLowerCase().replace(/[^0-9a-z-]/g, '-')
}

/** The id this template would produce today, whether or not it actually
 *  fires — used by the caller to check "does today's blast already exist"
 *  before paying for audience resolution. */
export function candidateId(template: Pick<RecurringCampaign, 'id'>, today: string): string {
  return `manual-${today}-${slugFor(template.id)}`
}

/**
 * Should this template produce a blast right now?
 *
 * `today` and `nowHHMM` are both America/New_York, both the caller's job to
 * compute — same division as `easternDate()` elsewhere in this codebase.
 * String comparison is enough for `nowHHMM >= template.fire_time`: both are
 * zero-padded `HH:MM`, so lexical order and time order agree.
 *
 * **Fires ONCE PER DAY, from the moment `fire_time` passes until midnight** —
 * not just in the instant it's reached. A generation job polling every
 * fifteen minutes (the plan; see `generate-scheduled.ts`) will see this
 * return `true` on every poll for the rest of the firing day, which is
 * correct and intended: the `candidateId` conflict is what stops a second
 * blast being made, not this function declining to answer `true` again. A
 * job that missed its exact window — a deploy, a dropped invocation — still
 * catches up on the next poll instead of silently skipping the day, which is
 * the same "a two-day alert window survives one dropped check" reasoning
 * `syncstatus.ts` already relies on.
 */
export function shouldFireNow(
  template: Pick<RecurringCampaign, 'is_enabled' | 'cadence' | 'fire_time'>,
  today: string,
  nowHHMM: string,
): boolean {
  if (!template.is_enabled) return false
  if (validateCadence(template.cadence)) return false
  if (!TIME_RE.test(template.fire_time)) return false
  if (!TIME_RE.test(nowHHMM)) return false
  if (!firesOn(template.cadence, today)) return false
  return nowHHMM >= template.fire_time
}

/**
 * What today's blast actually is, once `shouldFireNow` has said yes.
 *
 * Not merged into `shouldFireNow` itself, on purpose: resolving the audience
 * means walking the whole roster through `selectAgents`, and the caller only
 * wants to pay for that once it has also confirmed today's blast does not
 * already exist. Splitting the cheap gate from the expensive build is the
 * same shape `syncStatus`/`watchdogVerdict` uses for "should this alert" vs
 * "what should it say".
 */
export function buildBlast(
  template: RecurringCampaign,
  today: string,
  allAgents: readonly Agent[],
  ctx: FilterContext,
  /** The instant this was built, ISO — goes into `audience.resolved_at`,
   *  the caller's job to supply so this stays a pure function of its
   *  arguments rather than reading its own clock. */
  nowIso: string,
): GeneratedBlast {
  const ids = resolveScheduledAudienceIds(template.audience_rule, allAgents as Agent[], ctx)
  return {
    id: candidateId(template, today),
    name: template.name,
    subject: template.subject,
    body: template.body,
    audience: {
      type: 'list',
      agent_ids: ids,
      from: `scheduled:${template.id}`,
      resolved_at: nowIso,
    },
    source_campaign_id: template.id,
    occurrence_date: today,
  }
}
